// UTM capture for checkout attribution (port of aldoo-web-app/src/utils/utm.js).
// The last campaign visit is kept in localStorage and attached to
// store {checkout_payment}; cleared once a purchase completes.
const UTM_STORAGE_KEY = "aldoo_utm"
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]
// Google Ads clicks: gclid, and gbraid / wbraid from iOS. Kept apart from the
// utm codes, for Google's 90 days, so the order can be tied to the ad click
const CLICK_STORAGE_KEY = "aldoo_ad_click"
const CLICK_PARAMS = ["gclid", "gbraid", "wbraid"]
const CLICK_DAYS = 90
// a Meta ad click (fbclid), for the Conversions API's fbc when the pixel has not set _fbc
const META_CLICK_KEY = "aldoo_meta_click"

function captureClick(params) {
  const fbclid = params.get("fbclid")
  if (fbclid) {
    try {
      localStorage.setItem(META_CLICK_KEY, JSON.stringify({ fbclid: fbclid.slice(0, 400), at: Date.now() }))
    } catch {
      // storage unavailable
    }
  }
  const found = {}
  for (const key of CLICK_PARAMS) {
    const value = params.get(key)
    if (value) found[key] = value.slice(0, 400)
  }
  if (!Object.keys(found).length) return
  try {
    localStorage.setItem(CLICK_STORAGE_KEY, JSON.stringify({ ...found, at: Date.now() }))
  } catch {
    // storage unavailable
  }
}

function adClick() {
  try {
    const stored = JSON.parse(localStorage.getItem(CLICK_STORAGE_KEY) || "null")
    if (!stored || Date.now() - (stored.at || 0) > CLICK_DAYS * 86400000) return {}
    return Object.fromEntries(CLICK_PARAMS.filter((key) => stored[key]).map((key) => [key, stored[key]]))
  } catch {
    return {}
  }
}

// the visitor's Google Analytics client (_ga: "GA1.1.<client>") and session (_ga_<id>: "GS1.1.<session>…" / "GS2.1.s<session>$…")
function analyticsVisit() {
  try {
    const cookies = Object.fromEntries(document.cookie.split(/;\s*/).map((part) => [part.split("=")[0], decodeURIComponent(part.split("=").slice(1).join("="))]))
    const ga = cookies._ga ? cookies._ga.split(".").slice(-2).join(".") : null
    const sessionCookie = Object.entries(cookies).find(([name]) => /^_ga_[A-Z0-9]+$/.test(name))?.[1] || ""
    const session = sessionCookie.match(/^GS\d\.\d\.s?(\d+)/)?.[1] || null
    return { ...(ga ? { gaClientId: ga } : {}), ...(session ? { gaSessionId: session } : {}) }
  } catch {
    return {}
  }
}

// the Meta pixel's browser (_fbp) and ad click (_fbc, or fb.1.<time>.<fbclid> from a landing
// the pixel did not see), and the buyer's browser: the server's Purchase is matched with them
function metaVisit() {
  const out = {}
  try {
    const cookies = Object.fromEntries(document.cookie.split(/;\s*/).map((part) => [part.split("=")[0], decodeURIComponent(part.split("=").slice(1).join("="))]))
    if (cookies._fbp) out.fbp = cookies._fbp
    if (cookies._fbc) out.fbc = cookies._fbc
    if (!out.fbc) {
      const click = JSON.parse(localStorage.getItem(META_CLICK_KEY) || "null")
      if (click?.fbclid && Date.now() - (click.at || 0) < CLICK_DAYS * 86400000) out.fbc = `fb.1.${click.at}.${click.fbclid}`
    }
  } catch {
    // cookies or storage unavailable
  }
  if (typeof navigator !== "undefined" && navigator.userAgent) out.userAgent = navigator.userAgent.slice(0, 400)
  return out
}

// the visitor's cookie choice: the server's ad conversions carry it (Google uses an EEA
// visitor's conversion as it allows). Undecided where nobody is asked counts as given.
function consentChoice() {
  try {
    const stored = localStorage.getItem("tracking_consent")
    if (stored === "granted" || stored === "denied") return { consent: stored }
    return { consent: window.__consentRequired === false ? "granted" : "denied" }
  } catch {
    return {}
  }
}

export function captureUTM() {
  if (typeof window === "undefined") return
  const params = new URLSearchParams(window.location.search)
  captureClick(params)
  const found = {}
  for (const key of UTM_PARAMS) {
    const value = params.get(key)
    if (value) found[key] = value
  }
  if (Object.keys(found).length === 0) return
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found))
  } catch {
    // storage unavailable
  }
}

/**
 * Keep utm codes the page did not arrive with — a click on one of the site's
 * own ad placements navigates without them in the URL.
 */
export function rememberUTM(params) {
  if (typeof window === "undefined" || !params) return
  const found = {}
  for (const key of UTM_PARAMS) if (params[key]) found[key] = String(params[key])
  if (!Object.keys(found).length) return
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found))
  } catch {
    // storage unavailable
  }
}

/**
 * What the checkout tells the server about where the buyer came from:
 * { source, medium, campaign, term, content } of the last campaign visit, the
 * Google Ads click (gclid / gbraid / wbraid) and the analytics visit
 * (gaClientId, gaSessionId), the Meta pixel's visit (fbp, fbc), the browser
 * (userAgent) and the cookie choice (consent) — or null when there is none of it.
 */
export function getUTM() {
  if (typeof window === "undefined") return null
  const utm = {}
  try {
    const stored = JSON.parse(localStorage.getItem(UTM_STORAGE_KEY) || "null") || {}
    for (const key of UTM_PARAMS) if (stored[key]) utm[key.replace("utm_", "")] = stored[key]
  } catch {
    // storage unavailable: the click and the visit still go
  }
  Object.assign(utm, adClick(), analyticsVisit(), metaVisit(), consentChoice())
  return Object.keys(utm).length ? utm : null
}

export function clearUTM() {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY)
  } catch {
    // ignore
  }
}
